import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import axiosInstance from '@/lib/axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ProtectedRoute - Safari Desktop compatible version
 * 
 * Uses axiosInstance which adds Authorization header from localStorage.
 * This works even when Safari blocks cookies.
 */
export default function ProtectedRoute({ children }) {
  const [state, setState] = useState({
    loading: true,
    authenticated: false,
    user: null
  });

  useEffect(() => {
    let cancelled = false;
    
    const checkAuth = async () => {
      console.log('[ProtectedRoute] Checking auth with backend...');
      
      // Check if we have a token first
      const token = localStorage.getItem('session_token');
      if (!token) {
        console.log('[ProtectedRoute] No token in localStorage, redirecting');
        if (!cancelled) {
          setState({ loading: false, authenticated: false, user: null });
        }
        return;
      }
      
      try {
        // Use axiosInstance which adds Authorization header automatically
        const response = await axiosInstance.get(`${API}/auth/me`);
        
        console.log('[ProtectedRoute] Auth OK:', response.data.email);
        
        if (!cancelled) {
          setState({
            loading: false,
            authenticated: true,
            user: response.data
          });
        }
      } catch (error) {
        console.log('[ProtectedRoute] Auth failed:', error?.response?.status || error.message);
        
        // Clear invalid token
        localStorage.removeItem('session_token');
        
        if (!cancelled) {
          setState({
            loading: false,
            authenticated: false,
            user: null
          });
        }
      }
    };
    
    checkAuth();
    
    return () => { cancelled = true; };
  }, []);

  // Loading
  if (state.loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[#030305]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent"></div>
          <p className="mt-4 text-cyan-400 font-['Rajdhani']">Vérification...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!state.authenticated) {
    console.log('[ProtectedRoute] Redirecting to home');
    return <Navigate to="/" replace />;
  }

  // Authenticated
  return React.cloneElement(children, { user: state.user });
}

// Keep these exports for compatibility but they won't be used
export function setAuthToken(token) {
  try {
    localStorage.setItem('session_token', token);
    console.log('[Auth] Token saved to localStorage');
  } catch (e) {
    console.warn('[Auth] localStorage not available');
  }
}

export function clearAuthToken() {
  try {
    localStorage.removeItem('session_token');
  } catch (e) {
    console.error('[Auth] Failed to clear token:', e);
  }
}

export function getAuthToken() {
  try {
    return localStorage.getItem('session_token');
  } catch (e) {
    return null;
  }
}
